/**
 * Application Header Component
 * 
 * Features:
 * - Light AppBar with blur effect
 * - User avatar with initials
 * - Dropdown menu with logout option
 * - Admin navigation is handled by the sidebar in AppLayout
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Button,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useSudo } from '../hooks/useSudo';
import { AppLogo } from './AppLogo';

export function Header() {
  const { user, isAdmin, isLoadingAttributes, signOut } = useAuth();
  const { isSudoMode } = useSudo();
  const navigate = useNavigate();
  const location = useLocation();
  // In sudo mode, present the UI as if the user is a viewer
  const showAsViewer = isSudoMode || !isAdmin;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Check if we're on the viewer page
  const isOnViewerPage = location.pathname.startsWith('/viewer/');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setAnchorEl(null);
  };

  const handleLogout = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setAnchorEl(null);
    signOut();
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    const username = user?.username || '';
    if (username) {
      return username[0].toUpperCase();
    }
    return '?';
  };

  const getDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.username || 'User';
  };

  return (
    <AppBar position="sticky" color="default" elevation={0} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AppLogo size={32} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.03em',
              color: 'text.primary',
            }}
          >
            Looker Embed Portal
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Dashboards button - only for viewers (and admins in sudo mode) */}
          {showAsViewer && isOnViewerPage && (
            <Button
              startIcon={<DashboardIcon />}
              onClick={() => navigate('/')}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              Dashboards
            </Button>
          )}
          
          <Box
            onClick={handleMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              padding: 1,
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
          >
            {isLoadingAttributes ? (
              <Skeleton variant="circular" width={36} height={36} />
            ) : (
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: '#3d3d5c',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                }}
              >
                {getInitials()}
              </Avatar>
            )}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              {isLoadingAttributes ? (
                <>
                  <Skeleton variant="text" width={120} height={20} sx={{ mb: 0.25 }} />
                  <Skeleton variant="text" width={80} height={16} />
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                    {getDisplayName()}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: showAsViewer ? 'text.secondary' : 'secondary.main',
                      fontWeight: showAsViewer ? 400 : 500,
                    }}
                  >
                    {showAsViewer ? 'Viewer' : 'Administrator'}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            disableRestoreFocus
            slotProps={{
              paper: {
                sx: { minWidth: 200, mt: 1 },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              {isLoadingAttributes ? (
                <>
                  <Skeleton variant="text" width={120} height={20} sx={{ mb: 0.25 }} />
                  <Skeleton variant="text" width={80} height={16} />
                </>
              ) : (
                <>
                  <Typography variant="body2" fontWeight={500}>
                    {getDisplayName()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {showAsViewer ? 'Viewer' : 'Administrator'}
                  </Typography>
                </>
              )}
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
